import { Filter, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiRequest } from "../api";
import { DataTable } from "../components/DataTable";
import { DetailDrawer } from "../components/DetailDrawer";
import { PageHeader } from "../components/PageHeader";
import { Pagination } from "../components/Pagination";
import { ShowStoryHeader } from "../components/ShowStoryHeader";
import { ShowOperationLauncher } from "../components/ShowOperationLauncher";
import type { DataRecord } from "../types";
import type { Session } from "../types";
import type { ShowEngine, ShowOperation, ShowPage, ShowStory } from "../show-generator";
import { activeOperation } from "../show-generator";

const filters = [
  ["storyId", "Story ID"], ["engine", "Engine"], ["step", "Step"], ["name", "Command"], ["status", "Status"], ["active", "Active"],
] as const;

export function ShowOperationsPage({ session }: { session: Session }) {
  const { storyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState<Record<string, string>>(() => Object.fromEntries(filters.map(([key]) => [key, searchParams.get(key) ?? (key === "storyId" && storyId ? storyId : "")])));
  const [operations, setOperations] = useState<ShowOperation[]>([]);
  const [story, setStory] = useState<ShowStory | null>(null);
  const [engine, setEngine] = useState<ShowEngine | null>(null);
  const [selected, setSelected] = useState<DataRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const page = Math.max(1, Number(searchParams.get("page") ?? 1));
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const query = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (storyId) params.set("storyId", storyId);
    return params.toString();
  }, [page, searchParams, storyId]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const currentStory = storyId ? await apiRequest<ShowStory>(`/api/show-generator/stories/${storyId}`) : null;
      const [result, currentEngine] = await Promise.all([
        apiRequest<ShowPage<ShowOperation>>(`/api/show-generator/operations?${query}`),
        currentStory ? apiRequest<ShowEngine>(`/api/show-generator/engines/${encodeURIComponent(currentStory.engine)}`) : Promise.resolve(null),
      ]);
      setOperations(result.data);
      setTotal(result.pagination.total);
      setStory(currentStory);
      setEngine(currentEngine);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load operations");
    } finally { if (!quiet) setLoading(false); }
  }, [query, storyId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!operations.some((operation) => activeOperation(operation.status))) return;
    const timer = window.setInterval(() => void load(true), 3000);
    return () => window.clearInterval(timer);
  }, [load, operations]);

  function apply(event: React.FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    for (const [key, value] of Object.entries(draft)) if (value.trim()) next.set(key, value.trim());
    next.set("page", "1");
    setSearchParams(next);
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next);
  }

  async function inspect(row: DataRecord) {
    setError("");
    try {
      setSelected(await apiRequest<ShowOperation>(`/api/show-generator/operations/${String(row.id)}`) as unknown as DataRecord);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load operation details");
    }
  }

  const rows = operations.map((operation) => ({
    ...operation,
    price: typeof operation.price === "number" ? `$${operation.price.toFixed(6)}` : "-",
  })) as unknown as DataRecord[];
  return <>
    {story && engine ? <ShowOperationLauncher story={story} engine={engine} session={session} onOperationFinished={() => void load(true)} renderHeader={(controls) => <ShowStoryHeader story={story} actions={controls} />} /> : story ? <ShowStoryHeader story={story} /> : <PageHeader title="Story Operations" description="Asynchronous generation and deletion workflow history." onRefresh={() => void load()} refreshing={loading} />}
    <form className="filter-bar show-operation-filters" onSubmit={apply}><Filter size={17} aria-hidden="true" />{filters.filter(([key]) => !(key === "storyId" && storyId)).map(([key, label]) => <label key={key}><span>{label}</span>{key === "name" ? <select value={draft[key] ?? ""} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}><option value="">All</option><option value="generate">Generate</option><option value="delete">Delete</option></select> : key === "status" ? <select value={draft[key] ?? ""} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}><option value="">All</option>{["queued", "running", "waiting", "succeeded", "failed"].map((status) => <option value={status} key={status}>{status}</option>)}</select> : key === "active" ? <select value={draft[key] ?? ""} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}><option value="">All</option><option value="true">Active</option><option value="false">Terminal</option></select> : <input value={draft[key] ?? ""} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))} />}</label>)}<button className="secondary-button" type="submit"><Search size={16} />Apply</button></form>
    {error ? <div className="error-banner" role="alert">{error}</div> : null}
    <section className="data-surface" aria-busy={loading}>
      {loading && operations.length === 0 ? <div className="loading-state">Loading operations…</div> : <DataTable rows={rows} columns={[{ key: "id", label: "Operation" }, { key: "status", kind: "status" }, { key: "step" }, { key: "name", label: "Command" }, { key: "commandScope", label: "Target" }, { key: "price", label: "Cost" }, { key: "stage" }, { key: "updatedAt", label: "Updated", kind: "date" }]} rowKey={(row) => String(row.id)} onInspect={(row) => void inspect(row)} actions={(row) => !storyId ? <Link className="table-action" to={`/show-generator/stories/${String(row.storyId)}`}>Story</Link> : null} />}
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </section>
    <DetailDrawer title="Story operation" value={selected} onClose={() => setSelected(null)} />
  </>;
}
